import React, { useCallback, useEffect, useRef, useState } from "react";
import { Results, useResults, useSearch } from "@usereactify/search";
import debounce from "lodash.debounce";

import { ResultCardProduct } from "@app/scripts/reactify/results/ResultCardProduct";
import { Button } from "@app/scripts/react-general-components/Button";

const DEBOUCE_TIME = 400;

export const InstantSearch: React.FC = () => {
  const { setSearchTerm, searchTerm, submitSearchTerm, showInstantSearchResults, setShowInstantSearchResults } =
    useSearch();
  const { resultStats } = useResults();
  const [pageSize, setPageSize] = useState(4);
  const [open, setOpen] = useState(false);

  const [delayedShowResults, setDelayedShowResults] = useState(showInstantSearchResults);

  const ref = useRef(null);
  const resultsRef = useRef(null);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      submitSearchTerm(searchTerm);
    },
    [submitSearchTerm, searchTerm]
  );

  const clearSearch = useCallback(
    (e) => {
      e?.preventDefault();
      setSearchTerm("");
      setShowInstantSearchResults(false);
      if (ref?.current) {
        ref.current.value = "";
        ref.current.focus();
      }
    },
    [setSearchTerm, ref?.current]
  );

  const toggleOpen = useCallback(
    (e) => {
      const announceHeight =
        document.getElementById("shopify-section-announcement-bar")?.getBoundingClientRect()?.height || 0;
      const headerHeight = document.getElementById("shopify-section-header")?.getBoundingClientRect()?.height || 0;
      const searchWrap: HTMLElement | null = document.querySelector("[data-instantsearchwrap]");
      const overlay = document.querySelector(".instant-search-hide");

      const headerDrawer = document.querySelector("header-drawer");
      headerDrawer?.closeSubmenu(headerDrawer.querySelector("details"));

      if (!searchWrap) return;

      setOpen(!open);

      searchWrap.classList.toggle("visually-hidden");
      overlay?.classList.toggle("hidden");

      document.querySelectorAll("[data-toggleinstantsearch]")?.forEach((elem: Element) => {
        elem.querySelector(".icon-search")?.classList.toggle("hidden");
        elem.querySelector(".icon-close")?.classList.toggle("hidden");
      });

      document.body.classList.toggle("no-scroll");

      const position = window.scrollY > announceHeight ? "fixed" : "absolute";
      const height = window.scrollY > announceHeight ? headerHeight : headerHeight + announceHeight;

      searchWrap.style.position = position;
      searchWrap.style.top = height + "px";

      if (window.innerWidth >= 990) {
        searchWrap.style.maxHeight = `75vh`;
      } else {
        searchWrap.style.maxHeight = `calc(100vh - ${height}px)`;
      }
      if (!open) {
        clearSearch();
        ref?.current?.focus();
      }
    },
    [resultsRef, open, setOpen, clearSearch]
  );

  const search = debounce((e) => {
    setSearchTerm(e.target?.value);
    setShowInstantSearchResults(true);
  }, DEBOUCE_TIME);

  useEffect(() => {
    setTimeout(() => {
      setDelayedShowResults(showInstantSearchResults);
    }, DEBOUCE_TIME);
  }, [showInstantSearchResults]);

  useEffect(() => {
    const collections = document.querySelector("[data-instantcollections]");

    if (searchTerm && resultStats?.numberOfResults && resultStats.numberOfResults > 0) {
      collections?.classList.add("visually-hidden");
    } else {
      collections?.classList.remove("visually-hidden");
    }
  }, [resultStats?.numberOfResults, searchTerm]);

  useEffect(() => {
    const overlay = document.querySelector(".instant-search-hide");
    overlay?.addEventListener("click", toggleOpen);

    document.querySelectorAll("[data-toggleinstantsearch]")?.forEach((elem: Element) => {
      elem.addEventListener("click", toggleOpen);
    });

    return () => {
      overlay?.removeEventListener("click", toggleOpen);
      document.querySelectorAll("[data-toggleinstantsearch]")?.forEach((elem: Element) => {
        elem.removeEventListener("click", toggleOpen);
      });
    };
  }, []);

  const handleWindowDimensionChange = useCallback(() => {
    if (window.innerWidth >= 990 && pageSize === 4) {
      setPageSize(8);
    } else {
      setPageSize(4);
    }
  }, [pageSize]);

  useEffect(() => {
    handleWindowDimensionChange();
    window.addEventListener("resize", handleWindowDimensionChange);
    return () => window.removeEventListener("resize", handleWindowDimensionChange);
  }, []);

  return (
    <>
      <div className="page-width">
        <extended-search>
          <form onSubmit={handleSubmit} className="instant-search-form">
            <div className="_field">
              <input
                ref={ref}
                type="text"
                className="_field__input"
                onKeyUp={search}
                placeholder="Search products, categories, etc..."
                data-extendedinput
              />
              <button type="submit" className="_field__button left" aria-label="form button">
                <ui-icon name="icon-search" width="16" height="16"></ui-icon>
              </button>
              {searchTerm.length > 0 && (
                <Button
                  onClick={clearSearch}
                  label="Clear"
                  ariaLabel="Clear search term"
                  variant="text"
                  className="instant-search-form__clear"
                />
              )}
              <button onClick={toggleOpen} type="button" className="_field__button right" aria-label="form button">
                <ui-icon name="icon-close-large" width="16" height="16"></ui-icon>
              </button>
            </div>
          </form>
          <div className="extended-search-collection visually-hidden" data-relatedcollections />
        </extended-search>

        {delayedShowResults && (
          <Results
            pageSize={pageSize}
            listStyle={{}}
            listClassName="instant-search__results"
            includeFields={[
              "storefrontId",
              "presentment_price_ranges",
              "images",
              "discount_amount",
              "tags",
              "price_min",
              "price_max",
              "variants.*",
            ]}
            excludeFields={["image"]}
            renderResultCardProduct={ResultCardProduct}
            renderLoading={() => null}
            renderNoResults={NoResults}
          />
        )}

        {resultStats?.displayedResults && resultStats.displayedResults > 0 ? (
          <div className="instant-search__results-submit">
            <Button
              label={`View ${resultStats?.numberOfResults} Result${resultStats.displayedResults > 1 ? "s" : ""}`}
              onClick={() => submitSearchTerm(searchTerm)}
              variant="primary"
              color="text-white"
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

const NoResults = () => {
  const { searchTerm } = useSearch();

  useEffect(() => {
    const collections = document.querySelector("[data-instantcollections]");
    const collectionsTitle = document?.querySelector(".instant-search__no-results-title");
    collectionsTitle?.classList.add("hidden");
    collections?.classList.remove("visually-hidden");

    return () => {
      collections?.classList.remove("visually-hidden");
      if (!searchTerm) {
        collectionsTitle?.classList.remove("hidden");
      }
    };
  }, []);

  return (
    <div className="instant-search__no-rs-results text-md">
      No results for '{searchTerm}'. Try another search or browse popular categories.
    </div>
  );
};
